/*!
 * MONTVIELLE — product page (mv-product.js)
 *
 * Two option rows drive one hidden `id` input. The form stays a plain Shopify product form,
 * so mv-cart.js intercepts the submit exactly as it does everywhere else and the drawer opens.
 * This file never posts to the cart and never formats money: Liquid printed every variant's
 * price into the JSON blob, and the script swaps whole strings.
 */
(() => {
  'use strict';

  const pdp = document.querySelector('[data-mv-pdp]');
  if (!pdp) return;

  const form    = pdp.querySelector('.mv-buy__form');
  const idInput = pdp.querySelector('[data-mv-id]');
  const blob    = pdp.querySelector('[data-mv-variants]');
  const hasOpts = pdp.dataset.mvOpts === 'true';
  const twoUp   = pdp.dataset.mvTwoUp === 'true';

  let variants = [];
  try { variants = JSON.parse(blob ? blob.textContent : '[]'); } catch (e) { variants = []; }

  const track = (event, detail) =>
    document.dispatchEvent(new CustomEvent('mv:track', { detail: { event, angle: pdp.dataset.mvAngle, ...detail } }));

  const sw1   = Array.from(pdp.querySelectorAll('[data-mv-v1]'));
  const sw2   = Array.from(pdp.querySelectorAll('[data-mv-v2]'));
  const label = pdp.querySelector('[data-mv-opt-value="1"]');

  let chosen1 = null;
  let chosen2 = null;

  /* ------------------------------------------------------------------ *
   * 1. Matching
   * ------------------------------------------------------------------ */
  const match = () => variants.find((v) =>
    (!hasOpts) ||
    (v.o1 === chosen1 && (!twoUp || v.o2 === chosen2))
  ) || null;

  const firstFor = (v1) => variants.find((v) => v.o1 === v1) || null;

  /* Any variant of this colourway that can actually be bought. */
  const liveFor = (v1) => variants.find((v) => v.o1 === v1 && v.a) || null;

  /* ------------------------------------------------------------------ *
   * 2. Gallery — a native scroll-snap track
   *
   * The gesture is the browser's. This file only answers three questions: which slide is in
   * view (so the thumbnails and dots agree), where to go when an arrow or a thumbnail is
   * pressed, and which slide wears a given colourway. Nothing here runs during a swipe, which
   * is the whole point — the old version paged by cross-fading two 1200px photographs and
   * stuttered on exactly the frames the shopper was looking at.
   * ------------------------------------------------------------------ */
  const stage  = pdp.querySelector('[data-mv-stage]');
  const rail   = pdp.querySelector('[data-mv-track]');
  const slides = rail ? Array.from(rail.querySelectorAll('[data-mv-slide]')) : [];
  const thumbs = Array.from(pdp.querySelectorAll('[data-mv-thumb]'));
  const dots   = Array.from(pdp.querySelectorAll('[data-mv-dot]'));

  let at = 0;

  /* The neighbours on either side, told to load now. Lazy loading alone will fetch them, but
     it waits until they are nearly in the scrollport, and on a slow connection that is the
     difference between a photograph and a grey rectangle. */
  function warm(i) {
    if (!slides.length) return;
    /* Clamp, never wrap. Wrapping made slide 0 fetch slide 29 on a thirty-image goggle —
       the neighbours worth decoding are the ones about to be swiped to, not the far end. */
    for (let n = Math.max(0, i - 1); n <= Math.min(slides.length - 1, i + 1); n += 1) {
      const img = slides[n].querySelector('img');
      if (img && img.loading === 'lazy') img.loading = 'eager';
    }
  }

  function paintIndex(i) {
    if (i < 0) return;
    /* Warm before the early return, or the look-ahead never runs at boot: the page opens on
       slide 0 with `at` already 0, so the very first swipe found nothing decoded — which is
       the swipe the shopper judges the whole gallery by. */
    warm(i);
    if (i === at) return;
    at = i;
    thumbs.forEach((t) => {
      const on = Number(t.dataset.mvThumb) === i;
      t.classList.toggle('is-on', on);
      t.setAttribute('aria-current', String(on));
      /* Roving tabindex: one stop for the whole rail, arrows to move inside it. */
      t.tabIndex = on ? 0 : -1;
    });
    dots.forEach((d) => d.classList.toggle('is-on', Number(d.dataset.mvDot) === i));
    /* Thirty thumbnails are far taller than the rail, so the active one has to be brought
       to it or the highlight lands somewhere the shopper cannot see. */
    const thumb = thumbs[i];
    if (thumb && typeof thumb.scrollIntoView === 'function') {
      thumb.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  }

  /* While a programmatic scroll is in flight the observer sees every slide it passes and
     would repaint the thumbnails on each one. The destination is already known here. */
  let driving = 0;

  function goTo(i, behavior) {
    if (!rail || !slides.length) return;
    const n = Math.max(0, Math.min(i, slides.length - 1));
    warm(n);
    clearTimeout(driving);
    driving = setTimeout(() => { driving = 0; }, behavior === 'auto' ? 80 : 620);
    rail.scrollTo({ left: n * rail.clientWidth, behavior: behavior || 'smooth' });
    paintIndex(n);
  }

  /* No wrap. A snap track cannot wrap without a jump, and stopping at the end is what the
     native gesture already does — you cannot swipe past the last photograph either. */
  function step(dir) {
    if (!slides.length) return;
    goTo(at + dir, 'smooth');
  }

  /* Which slide is actually in view — the source of truth once the shopper's thumb is on it. */
  if (rail && slides.length > 1) {
    if ('IntersectionObserver' in window) {
      const spy = new IntersectionObserver(
        (entries) => {
          if (driving) return;
          entries.forEach((entry) => {
            if (entry.isIntersecting && entry.intersectionRatio > 0.55) paintIndex(slides.indexOf(entry.target));
          });
        },
        { root: rail, threshold: [0.56] },
      );
      slides.forEach((slide) => spy.observe(slide));
    } else {
      let ticking = false;
      rail.addEventListener('scroll', () => {
        if (ticking || driving) return;
        ticking = true;
        requestAnimationFrame(() => {
          ticking = false;
          paintIndex(Math.round(rail.scrollLeft / Math.max(rail.clientWidth, 1)));
        });
      }, { passive: true });
    }
  }

  function showImageId(imgId, _fallbackUrl, behavior) {
    const i = slides.findIndex((slide) => slide.dataset.mvImg && slide.dataset.mvImg === String(imgId));
    if (i >= 0) goTo(i, behavior);
  }

  pdp.addEventListener('click', (e) => {
    const thumb = e.target.closest('[data-mv-thumb]');
    if (thumb) { goTo(Number(thumb.dataset.mvThumb), 'smooth'); return; }
    if (e.target.closest('[data-mv-prev]')) { step(-1); return; }
    if (e.target.closest('[data-mv-next]')) { step(1); return; }
  });

  /* Arrow keys inside the thumbnail rail, as a toolbar owes its user. */
  const railEl = pdp.querySelector('[data-mv-rail]');
  if (railEl && thumbs.length > 1) {
    railEl.addEventListener('keydown', (e) => {
      const moves = { ArrowUp: -1, ArrowLeft: -1, ArrowDown: 1, ArrowRight: 1 };
      let next = null;
      if (e.key in moves) next = at + moves[e.key];
      else if (e.key === 'Home') next = 0;
      else if (e.key === 'End') next = thumbs.length - 1;
      if (next === null) return;
      e.preventDefault();
      goTo(Math.max(0, Math.min(next, thumbs.length - 1)), 'smooth');
      const focus = thumbs[at];
      if (focus) focus.focus({ preventScroll: true });
    });
  }

  /* A resize changes what one page is worth, so hold the slide the shopper was on. */
  if (rail) {
    let width = rail.clientWidth;
    window.addEventListener('resize', () => {
      if (rail.clientWidth === width) return;
      width = rail.clientWidth;
      rail.scrollTo({ left: at * width, behavior: 'auto' });
    }, { passive: true });
  }

  /* ------------------------------------------------------------------ *
   * 3. Painting the buy box
   * ------------------------------------------------------------------ */
  const priceEl  = pdp.querySelector('[data-mv-price]');
  const stickyP  = pdp.querySelector('[data-mv-sticky-price]');
  const chosenEl = pdp.querySelector('[data-mv-chosen]');
  const atcLabels = Array.from(pdp.querySelectorAll('[data-mv-atc-label]'));
  const submits   = Array.from(pdp.querySelectorAll('[data-mv-submit]'));
  const addLabel  = atcLabels.length ? atcLabels[0].textContent.trim() : 'Add to bag';
  const sizeBox   = pdp.querySelector('[data-mv-opt="2"]');

  function paint() {
    /* Colourway row */
    sw1.forEach((b) => {
      const on = b.dataset.mvV1 === chosen1;
      b.classList.toggle('is-on', on);
      b.setAttribute('aria-checked', String(on));
      const live1 = liveFor(b.dataset.mvV1);
      b.classList.toggle('is-out', !live1);
    });
    if (label) label.textContent = chosen1 || '';

    /* Size row: availability is recomputed against the chosen colourway. */
    if (twoUp) {
      sw2.forEach((b) => {
        const v = variants.find((x) => x.o1 === chosen1 && x.o2 === b.dataset.mvV2);
        const out = !v || !v.a;
        b.classList.toggle('is-out', out);
        const on = b.dataset.mvV2 === chosen2 && !out;
        b.classList.toggle('is-on', on);
        b.setAttribute('aria-checked', String(on));
      });
      if (chosen2) {
        const still = variants.find((x) => x.o1 === chosen1 && x.o2 === chosen2 && x.a);
        if (!still) chosen2 = null;
      }
    }

    const v = match();

    if (v) {
      if (idInput) idInput.value = v.id;
      if (priceEl) priceEl.textContent = v.p;
      if (stickyP) stickyP.textContent = v.p;
      if (chosenEl) chosenEl.textContent = [v.o1, v.o2].filter(Boolean).join(' · ');
      const url = new URL(location.href);
      url.searchParams.set('variant', v.id);
      history.replaceState(null, '', url.toString());
    } else if (chosen1) {
      const near = firstFor(chosen1);
      if (near && priceEl) priceEl.textContent = near.p;
      if (near && stickyP) stickyP.textContent = near.p;
      if (chosenEl) chosenEl.textContent = chosen1;
    }

    /* One button. Its label says what the next step is. */
    const needSize = hasOpts && twoUp && !chosen2;
    atcLabels.forEach((el) => {
      el.textContent = needSize ? 'Select your size' : (v && v.a ? `${addLabel} — ${v.p}` : (v ? 'Sold out' : addLabel));
    });
    submits.forEach((b) => {
      b.classList.toggle('is-ask', needSize);
      b.disabled = !needSize && !(v && v.a);
    });
  }

  /* ------------------------------------------------------------------ *
   * 4. Choosing
   * ------------------------------------------------------------------ */
  pdp.addEventListener('click', (e) => {
    const a = e.target.closest('[data-mv-v1]');
    if (a) {
      chosen1 = a.dataset.mvV1;
      /* A colourway is a change of subject, not a journey: jump straight to it rather than
         sweeping the shopper past twenty photographs of the colours in between. */
      showImageId(a.dataset.mvImgId, a.dataset.mvImgUrl, 'auto');
      paint();
      track('select_option', { option: 1, value: chosen1 });
      return;
    }
    const b = e.target.closest('[data-mv-v2]');
    if (b) {
      if (b.classList.contains('is-out')) return;
      chosen2 = b.dataset.mvV2;
      paint();
      track('select_option', { option: 2, value: chosen2 });
    }
  });

  /* The button is the affordance: when a size is missing it takes you to the size row. */
  submits.forEach((btn) => btn.addEventListener('click', (e) => {
    if (!btn.classList.contains('is-ask') || !sizeBox) return;
    e.preventDefault();
    sizeBox.scrollIntoView({ behavior: 'smooth', block: 'center' });
    sizeBox.classList.remove('is-asking');
    requestAnimationFrame(() => sizeBox.classList.add('is-asking'));
    const first = sw2.find((s) => !s.classList.contains('is-out'));
    if (first) first.focus({ preventScroll: true });
  }));

  /* Arrow keys inside a radiogroup, because these are role=radio buttons. */
  pdp.addEventListener('keydown', (e) => {
    if (!['ArrowRight', 'ArrowLeft', 'ArrowUp', 'ArrowDown'].includes(e.key)) return;
    const row = e.target.closest('.mv-opt__row');
    if (!row) return;
    const items = Array.from(row.querySelectorAll('[role="radio"]')).filter((i) => !i.classList.contains('is-out'));
    const i = items.indexOf(e.target);
    if (i < 0) return;
    e.preventDefault();
    const next = items[(i + (e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length];
    next.focus();
    next.click();
  });

  /* ------------------------------------------------------------------ *
   * 5. Quantity
   * ------------------------------------------------------------------ */
  const qty = pdp.querySelector('[data-mv-qty]');
  if (qty) {
    const input = qty.querySelector('.mv-qty__input');
    qty.addEventListener('click', (e) => {
      const up = e.target.closest('[data-mv-qty-up]');
      const down = e.target.closest('[data-mv-qty-down]');
      if (!up && !down) return;
      const n = parseInt(input.value, 10) || 1;
      input.value = Math.min(10, Math.max(1, n + (up ? 1 : -1)));
    });
  }

  /* ------------------------------------------------------------------ *
   * 6. Sticky bar
   * ------------------------------------------------------------------ */
  const sticky  = pdp.querySelector('[data-mv-sticky]');
  const actions = pdp.querySelector('[data-mv-actions]');
  if (sticky && actions && 'IntersectionObserver' in window) {
    sticky.hidden = false;
    new IntersectionObserver(([entry]) => {
      sticky.classList.toggle('is-up', !entry.isIntersecting && entry.boundingClientRect.top < 0);
    }, { threshold: 0 }).observe(actions);
  }

  /* ------------------------------------------------------------------ *
   * 7. Size guide
   * ------------------------------------------------------------------ */
  const guide = pdp.querySelector('[data-mv-guide]');
  if (guide) {
    pdp.addEventListener('click', (e) => {
      if (e.target.closest('[data-mv-guide-open]')) {
        if (typeof guide.showModal === 'function') guide.showModal();
        else guide.setAttribute('open', '');
        track('view_size_guide', {});
      } else if (e.target.closest('[data-mv-guide-close]')) {
        guide.close();
      }
    });
    guide.addEventListener('click', (e) => { if (e.target === guide) guide.close(); });
  }

  /* ------------------------------------------------------------------ *
   * 8. "Back to the buy box" from a band further down
   * ------------------------------------------------------------------ */
  document.addEventListener('click', (e) => {
    if (!e.target.closest('[data-mv-scroll-buy]')) return;
    const target = sizeBox || actions;
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'center' });
  });

  /* ------------------------------------------------------------------ *
   * 9. Complete the kit — "add them together"
   *    Fills a real product form with items[N][id] and submits it, so the add travels the
   *    same path as every other add and the drawer handles the rest.
   * ------------------------------------------------------------------ */
  const bulk = document.querySelector('[data-mv-bulk]');
  if (bulk) {
    const slots = bulk.querySelector('[data-mv-bulk-slots]');
    const msg   = bulk.querySelector('[data-mv-bulk-msg]');
    const go    = bulk.querySelector('[data-mv-bulk-go]');
    const total = document.querySelector('[data-mv-bulk-total]');

    const picked = () => Array.from(document.querySelectorAll('[data-mv-comp]'))
      .filter((c) => { const t = c.querySelector('[data-mv-comp-take]'); return t && t.checked; });

    /* The only place on this site that composes money, because the total depends on which
       boxes are ticked and Liquid cannot pre-render every combination. The currency comes
       from Liquid; Intl handles the separators that `toFixed(2)` with a hard-coded dollar
       sign did not — that version printed "$1247.00" for a kit costing $1,247.00. */
    const currency = (total && total.dataset.mvCurrency) || 'USD';
    let nf = null;
    try {
      nf = new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency });
    } catch (e) {
      nf = null;
    }
    const money = (cents) => (nf ? nf.format(cents / 100) : `${(cents / 100).toFixed(2)} ${currency}`);

    /* A chosen size can cost more than the "from" price, so the card's price follows it. */
    function cardCents(card) {
      const sel = card.querySelector('[data-mv-comp-size]');
      const opt = sel && sel.selectedIndex > 0 ? sel.options[sel.selectedIndex] : null;
      if (opt && opt.dataset.price) return parseInt(opt.dataset.price, 10) || 0;
      return parseInt(card.dataset.mvCompPrice, 10) || 0;
    }

    function retotal() {
      const chosenCards = picked();
      if (go) go.disabled = chosenCards.length === 0;
      if (!total) return;
      if (!chosenCards.length) {
        total.textContent = total.dataset.mvBulkEmpty || 'Select what you want';
        return;
      }
      const cents = chosenCards.reduce((s, c) => s + cardCents(c), 0);
      const tpl = total.dataset.mvBulkTotalTemplate || 'Add selected — %s';
      total.textContent = tpl.replace('%s', money(cents));
    }

    if (total && !total.dataset.mvBulkEmpty) total.dataset.mvBulkEmpty = total.textContent.trim();

    document.addEventListener('change', (e) => {
      const card = e.target.closest('[data-mv-comp]');
      if (!card) return;
      card.classList.remove('is-asking');
      /* Choosing a size is intent: it ticks the box for you. */
      if (e.target.matches('[data-mv-comp-size]') && e.target.value) {
        const take = card.querySelector('[data-mv-comp-take]');
        if (take && !take.checked) take.checked = true;
      }
      card.classList.toggle('is-picked', !!card.querySelector('[data-mv-comp-take]:checked'));
      retotal();
    });
    retotal();

    if (go) go.addEventListener('click', () => {
      const ids = [];
      let firstMissing = null;

      for (const card of picked()) {
        if (card.dataset.mvCompSized === 'true') {
          const sel = card.querySelector('[data-mv-comp-size]');
          if (!sel || !sel.value) { card.classList.add('is-asking'); if (!firstMissing) firstMissing = card; continue; }
          ids.push(sel.value);
        } else {
          const fixed = card.querySelector('[data-mv-comp-fixed]');
          if (fixed && fixed.value) ids.push(fixed.value);
        }
      }

      if (firstMissing) {
        if (msg) { msg.textContent = 'Pick a size on the highlighted piece and we will add everything together.'; msg.hidden = false; }
        firstMissing.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      if (!ids.length) return;
      if (msg) msg.hidden = true;

      slots.textContent = '';
      ids.forEach((id, i) => {
        const a = document.createElement('input');
        a.type = 'hidden'; a.name = `items[${i}][id]`; a.value = id;
        const b = document.createElement('input');
        b.type = 'hidden'; b.name = `items[${i}][quantity]`; b.value = '1';
        slots.append(a, b);
      });

      track('add_kit', { count: ids.length });
      if (typeof bulk.requestSubmit === 'function') bulk.requestSubmit();
      else bulk.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    });
  }

  /* ------------------------------------------------------------------ *
   * 10. Boot — open on the variant in the URL, else the dearest live colourway
   * ------------------------------------------------------------------ */
  (function boot() {
    if (!hasOpts) { paint(); return; }
    const want = new URL(location.href).searchParams.get('variant');
    let start = want ? variants.find((v) => String(v.id) === String(want)) : null;
    /* Open on the product, not on an accessory. The supplier's variant order is arbitrary,
       and where a listing carries a complete item alongside a cheaper spare — the Cirrus
       sells goggles and replacement lenses under one handle — the spare came first, so the
       page greeted you with a bare lens captioned "Only lens, no ski goggles". The dearest
       live variant is the thing the page is actually selling; the featured image breaks ties. */
    if (!start) {
      const live = variants.filter((v) => v.a);
      const pool = live.length ? live : variants;
      let top = 0;
      pool.forEach((v) => { if ((v.c || 0) > top) top = v.c || 0; });
      const best = pool.filter((v) => (v.c || 0) === top);
      const hero = pdp.dataset.mvHero;
      start = best.find((v) => String(v.img) === String(hero)) || best[0];
    }
    if (start) {
      chosen1 = start.o1 || null;
      if (want && start.o2) chosen2 = start.o2;
      /* Slide zero is the picked-by-hand hero. When it is not itself a colourway shot, leave
         it showing — the first thing a visitor sees should be the photograph we chose, not
         whichever packaging card the supplier attached to the first colour. Any swatch click
         moves off it immediately. */
      const leadOnHero = pdp.dataset.mvHerolead === '1' && !want;
      const sw = sw1.find((b) => b.dataset.mvV1 === chosen1);
      if (sw && !leadOnHero) showImageId(sw.dataset.mvImgId, sw.dataset.mvImgUrl, 'auto');
    }
    paint();
  })();

  track('view_item', { product_id: pdp.dataset.mvProduct || '', handle: pdp.dataset.mvHandle || '' });
})();
